import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const STORAGE_KEY = 'cobstream:player:extraBuffer';

function readPref(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function writePref(value: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    if (value) window.localStorage.setItem(STORAGE_KEY, '1');
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // storage quota / disabled — nothing actionable
  }
}

/** Hook callers use to read + react to the persisted pref. */
export function useExtraBufferPref(): { value: boolean; setValue: (v: boolean) => void } {
  const [value, setState] = useState<boolean>(() => readPref());
  // Stay in sync if another tab toggles the pref.
  useEffect(() => {
    function onStorage(e: StorageEvent): void {
      if (e.key !== STORAGE_KEY) return;
      setState(readPref());
    }
    window.addEventListener('storage', onStorage);
    return (): void => { window.removeEventListener('storage', onStorage); };
  }, []);
  function setValue(v: boolean): void {
    writePref(v);
    setState(v);
  }
  return { value, setValue };
}

interface Props {
  value: boolean;
  onChange: (v: boolean) => void;
}

/**
 * Small in-player toggle for the "Extra buffer" pref. Wired below the source
 * picker on Watch / WatchSports. Persisted to localStorage by `useExtraBufferPref`.
 */
export function BufferControl({ value, onChange }: Props): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="buffer-control" data-testid="buffer-control">
      <label className="buffer-control__row">
        <input
          type="checkbox"
          checked={value}
          onChange={(e): void => onChange(e.target.checked)}
          data-testid="buffer-control-toggle"
        />
        <span className="buffer-control__label">
          {t('player.buffer.label')}
        </span>
        <span className="buffer-control__hint">
          {t('player.buffer.hint')}
        </span>
      </label>
    </div>
  );
}
